import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { visualStyles, styleHelpers } from "@/lib/visual-consistency-utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Heart, 
  Phone, 
  Users, 
  AlertCircle, 
  Shield, 
  Bell,
  Plus, 
  Trash2,
  Upload,
  Key,
  UserCheck,
  Home,
  MapPin,
  Mail,
  MessageSquare
} from "lucide-react";
import { formatPhoneNumber } from "@/lib/format";
import { Parent } from "../../types/models";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// US States for address selection
const usStates = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

interface EmergencyContact {
  id?: string;
  name: string;
  relationship: string;
  phone: string;
  is_primary: boolean;
}

interface CommunicationPreferences {
  email_notifications: boolean;
  sms_notifications: boolean;
  class_reminders: boolean;
  billing_alerts: boolean;
  event_announcements: boolean;
  preferred_contact_method: 'email' | 'sms' | 'both';
}

interface ParentFormData {
  first_name: string | undefined;
  last_name: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  is_primary: boolean | undefined;
  is_active: boolean | undefined;
  
  // Address information
  address_street: string | undefined;
  address_city: string | undefined;
  address_state: string | undefined;
  address_zip: string | undefined;
  address_country: string | undefined;
  
  // Parent-specific data
  profile_photo_url: string | undefined;
  emergency_contacts: EmergencyContact[] | undefined;
  communication_preferences: CommunicationPreferences | undefined;
  notes: string | undefined;
  
  // Portal access
  portal_access_enabled: boolean | undefined;
  can_view_billing: boolean | undefined;
  can_manage_children: boolean | undefined;
}

interface ParentFormFieldsProps {
  formData: ParentFormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSwitchChange?: (name: string, checked: boolean) => void;
  handleImageUpload?: (file: File) => Promise<void>;
  onDelete?: (parent: Parent) => void;
  parent: Parent;
}

const ParentFormFields: React.FC<ParentFormFieldsProps> = ({
  formData,
  handleChange,
  handleSwitchChange,
  handleImageUpload,
  onDelete,
  parent,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(formData.profile_photo_url || null);
  const [formattedPhone, setFormattedPhone] = useState(formData.phone || '');
  const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContact[]>(
    formData.emergency_contacts || [{name: '', relationship: '', phone: '', is_primary: true}]
  );
  const [communicationPrefs, setCommunicationPrefs] = useState<CommunicationPreferences>(
    formData.communication_preferences || {
      email_notifications: true,
      sms_notifications: false,
      class_reminders: true,
      billing_alerts: true,
      event_announcements: true,
      preferred_contact_method: 'email'
    }
  );

  // Format phone number when the component mounts or formData.phone changes
  useEffect(() => {
    if (formData.phone) {
      setFormattedPhone(formatPhoneNumber(formData.phone));
    }
  }, [formData.phone]);

  // Initialize emergency contacts from props
  useEffect(() => {
    if (formData.emergency_contacts && formData.emergency_contacts.length > 0) {
      setEmergencyContacts(formData.emergency_contacts);
    }
  }, [formData.emergency_contacts]);

  const onImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Set preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
    
    // Upload file if handler provided
    if (handleImageUpload) {
      await handleImageUpload(file);
    }
  };

  const onSwitchChange = (name: string, checked: boolean) => {
    if (handleSwitchChange) {
      handleSwitchChange(name, checked);
    }
  };

  // Handle phone input with formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setFormattedPhone(formatted);
    
    // Create a synthetic event to pass to handleChange
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        name: 'phone',
        value: formatted
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    handleChange(syntheticEvent);
  };

  // Emergency contact handlers
  const addEmergencyContact = () => {
    const newContacts = [...emergencyContacts, {
      name: '',
      relationship: '',
      phone: '',
      is_primary: false
    }];
    setEmergencyContacts(newContacts);
  };

  const removeEmergencyContact = (index: number) => {
    if (emergencyContacts.length <= 1) return;
    
    const newContacts = emergencyContacts.filter((_, i) => i !== index);
    
    // If we removed the primary contact and there are other contacts left,
    // make the first one primary
    if (emergencyContacts[index].is_primary && newContacts.length > 0) {
      newContacts[0].is_primary = true;
    }
    
    setEmergencyContacts(newContacts);
  };

  const updateEmergencyContact = (index: number, field: keyof EmergencyContact, value: any) => {
    const updatedContacts = [...emergencyContacts];
    updatedContacts[index] = { ...updatedContacts[index], [field]: value };
    
    // If setting as primary, update other contacts to not be primary
    if (field === 'is_primary' && value === true) {
      updatedContacts.forEach((contact, i) => {
        if (i !== index) {
          contact.is_primary = false;
        }
      });
    }
    
    setEmergencyContacts(updatedContacts);
  };

  // Communication preference handlers
  const updateCommunicationPref = (field: keyof CommunicationPreferences, value: any) => {
    setCommunicationPrefs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || 'P';
    const last = lastName?.charAt(0) || 'A';
    return `${first}${last}`.toUpperCase();
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(parent);
    }
  };

  return (
    <div className="space-y-8">
      {/* Personal Information */}
      <Card className={styleHelpers.getCardClasses(false, "")}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
              <Heart className="h-4 w-4 text-accent-foreground" />
            </div>
            <h3 className={visualStyles.typography.cardTitle}>Personal Information</h3>
          </div>
          
          <div className="grid gap-6">
            {/* Profile Photo */}
            <div className="flex items-center gap-6">
              <div className="relative">
                {previewUrl ? (
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={previewUrl} alt="Parent photo" />
                    <AvatarFallback className="bg-gradient-to-br from-accent to-accent/80 text-accent-foreground text-lg">
                      {getInitials(formData.first_name, formData.last_name)}
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <Avatar className="h-20 w-20">
                    <AvatarFallback className="bg-gradient-to-br from-accent to-accent/80 text-accent-foreground text-lg">
                      {getInitials(formData.first_name, formData.last_name)}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
              
              <div className="flex-1">
                <Label htmlFor="profile_photo" className="text-sm font-medium">Profile Photo</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="profile_photo"
                    name="profile_photo"
                    type="file"
                    accept="image/*"
                    onChange={onImageChange}
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="sm">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </Button>
                </div>
              </div>
            </div>

            {/* Name and Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name || ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name || ""}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email || ""}
                  onChange={handleChange}
                  placeholder="parent@example.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formattedPhone}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  maxLength={14}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Family Connection */}
      <Card className={styleHelpers.getCardClasses(false, "")}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
              <Users className="h-4 w-4 text-accent-foreground" />
            </div>
            <h3 className={visualStyles.typography.cardTitle}>Family Connection</h3>
          </div>
          
          <div className="grid gap-4">
            {/* Primary Contact Status */}
            <div className="flex items-center justify-between space-x-2 border p-3 rounded-md bg-accent/20">
              <div>
                <Label htmlFor="is_primary" className="text-base">Primary Contact</Label>
                <p className="text-sm text-muted-foreground">Main contact for family communications</p>
              </div>
              <Switch
                id="is_primary"
                checked={formData.is_primary === true}
                onCheckedChange={(checked) => onSwitchChange('is_primary', checked)}
              />
            </div>

            {/* Family Information Display */}
            <div className="bg-accent/10 border rounded-lg p-4">
              <h4 className="text-sm font-semibold text-foreground mb-2">Family Details</h4>
              <div className="text-sm text-muted-foreground">
                <p><strong>Family ID:</strong> {parent.family_id?.slice(-8) || 'Not assigned'}</p>
                <p><strong>Role:</strong> {formData.is_primary ? 'Primary Contact' : 'Secondary Contact'}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  💡 Primary contacts receive all family communications and have full access to manage children.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Parent Portal Access */}
      <Card className={styleHelpers.getCardClasses(false, "")}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
              <Key className="h-4 w-4 text-accent-foreground" />
            </div>
            <h3 className={visualStyles.typography.cardTitle}>Parent Portal Access</h3>
          </div>
          
          <div className="grid gap-4">
            {/* Active Status */}
            <div className="flex items-center justify-between space-x-2 border p-3 rounded-md bg-accent/20">
              <div>
                <Label htmlFor="is_active" className="text-base">Account Status</Label>
                <p className="text-sm text-muted-foreground">
                  {formData.is_active ? "Active - Can access parent portal" : "Inactive - Cannot access parent portal"}
                </p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active !== false}
                onCheckedChange={(checked) => onSwitchChange('is_active', checked)}
              />
            </div>

            {/* Portal Permissions */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">Portal Permissions</h4>
              
              <div className="grid gap-3">
                <div className="flex items-center justify-between space-x-2 border p-2 rounded-md bg-card">
                  <div>
                    <Label htmlFor="portal_access_enabled" className="text-sm">Portal Access</Label>
                    <p className="text-xs text-muted-foreground">Can log into parent portal</p>
                  </div>
                  <Switch
                    id="portal_access_enabled"
                    checked={formData.portal_access_enabled !== false}
                    onCheckedChange={(checked) => onSwitchChange('portal_access_enabled', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between space-x-2 border p-2 rounded-md bg-card">
                  <div>
                    <Label htmlFor="can_view_billing" className="text-sm">View Billing</Label>
                    <p className="text-xs text-muted-foreground">Can view invoices and payment history</p>
                  </div>
                  <Switch
                    id="can_view_billing"
                    checked={formData.can_view_billing !== false}
                    onCheckedChange={(checked) => onSwitchChange('can_view_billing', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between space-x-2 border p-2 rounded-md bg-card">
                  <div>
                    <Label htmlFor="can_manage_children" className="text-sm">Manage Children</Label>
                    <p className="text-xs text-muted-foreground">Can update child information and schedules</p>
                  </div>
                  <Switch
                    id="can_manage_children"
                    checked={formData.can_manage_children !== false}
                    onCheckedChange={(checked) => onSwitchChange('can_manage_children', checked)}
                  />
                </div>
              </div>
            </div>

            {!formData.is_active && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <p className="text-sm text-amber-800">Inactive parents cannot login or receive notifications</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Address Information */}
      <Card className={styleHelpers.getCardClasses(false, "")}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
              <Home className="h-4 w-4 text-accent-foreground" />
            </div>
            <h3 className={visualStyles.typography.cardTitle}>Address Information</h3>
          </div>
          
          <div className="grid gap-4">
            <div className="text-sm text-muted-foreground bg-accent/10 p-2 rounded-md">
              💡 This address can be shared with students and used for pickup/dropoff authorization.
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address_street">Street Address</Label>
              <Input
                id="address_street"
                name="address_street"
                value={formData.address_street || ""}
                onChange={handleChange}
                placeholder="123 Main St"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="address_city">City</Label>
                <Input
                  id="address_city"
                  name="address_city"
                  value={formData.address_city || ""}
                  onChange={handleChange}
                  placeholder="City"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address_state">State</Label>
                <Select
                  value={formData.address_state || ""}
                  onValueChange={(value) => {
                    const syntheticEvent = {
                      target: { name: 'address_state', value }
                    } as React.ChangeEvent<HTMLInputElement>;
                    handleChange(syntheticEvent);
                  }}
                >
                  <SelectTrigger id="address_state">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {usStates.map(state => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address_zip">ZIP Code</Label>
                <Input
                  id="address_zip"
                  name="address_zip"
                  value={formData.address_zip || ""}
                  onChange={handleChange}
                  placeholder="12345"
                  maxLength={10}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contacts */}
      <Card className={styleHelpers.getCardClasses(false, "border-destructive/20 bg-destructive/5")}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-destructive/20 rounded-full flex items-center justify-center">
              <AlertCircle className="h-4 w-4 text-destructive" />
            </div>
            <h3 className={visualStyles.typography.cardTitle} style={{color: 'hsl(var(--destructive))'}}>Emergency Contacts</h3>
          </div>
          
          <div className="grid gap-4">
            <div className="text-sm text-destructive bg-destructive/10 p-2 rounded-md">
              ⚠️ These are emergency contacts for the parent, not the children. Child emergency contacts are managed separately.
            </div>
            
            {emergencyContacts.map((contact, index) => (
              <div key={index} className="border border-destructive/20 bg-white rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-semibold text-destructive">Emergency Contact {index + 1}</h4>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => removeEmergencyContact(index)}
                    disabled={emergencyContacts.length <= 1}
                    className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
                
                <div className="grid gap-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor={`ec-name-${index}`}>Name</Label>
                      <Input
                        id={`ec-name-${index}`}
                        value={contact.name}
                        onChange={(e) => updateEmergencyContact(index, 'name', e.target.value)}
                        placeholder="Full Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`ec-relationship-${index}`}>Relationship</Label>
                      <Input
                        id={`ec-relationship-${index}`}
                        value={contact.relationship}
                        onChange={(e) => updateEmergencyContact(index, 'relationship', e.target.value)}
                        placeholder="Spouse, Sibling, etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`ec-phone-${index}`}>Phone</Label>
                      <Input
                        id={`ec-phone-${index}`}
                        value={contact.phone}
                        onChange={(e) => updateEmergencyContact(index, 'phone', formatPhoneNumber(e.target.value))}
                        placeholder="(555) 123-4567"
                        maxLength={14}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between space-x-2 border border-destructive/20 p-2 rounded-md bg-destructive/5">
                    <div>
                      <Label htmlFor={`ec-primary-${index}`} className="text-sm">Primary Contact</Label>
                      <p className="text-xs text-muted-foreground">Contact first in emergency</p>
                    </div>
                    <Switch
                      id={`ec-primary-${index}`}
                      checked={contact.is_primary}
                      onCheckedChange={(checked) => updateEmergencyContact(index, 'is_primary', checked)}
                    />
                  </div>
                </div>
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              className="mt-2 border-destructive/20 text-destructive hover:bg-destructive/10"
              onClick={addEmergencyContact}
            >
              <Plus size={16} className="mr-2" />
              Add Emergency Contact
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Communication Preferences */}
      <Card className={styleHelpers.getCardClasses(false, "")}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
              <Bell className="h-4 w-4 text-accent-foreground" />
            </div>
            <h3 className={visualStyles.typography.cardTitle}>Communication Preferences</h3>
          </div>
          
          <div className="grid gap-4">
            {/* Notification Types */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">Notification Types</h4>
              
              <div className="grid gap-3">
                <div className="flex items-center justify-between space-x-2 border p-2 rounded-md bg-card">
                  <div>
                    <Label htmlFor="email_notifications" className="text-sm">Email Notifications</Label>
                    <p className="text-xs text-muted-foreground">Receive notifications via email</p>
                  </div>
                  <Switch
                    id="email_notifications"
                    checked={communicationPrefs.email_notifications}
                    onCheckedChange={(checked) => updateCommunicationPref('email_notifications', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between space-x-2 border p-2 rounded-md bg-card">
                  <div>
                    <Label htmlFor="sms_notifications" className="text-sm">SMS Notifications</Label>
                    <p className="text-xs text-muted-foreground">Receive notifications via text message</p>
                  </div>
                  <Switch
                    id="sms_notifications"
                    checked={communicationPrefs.sms_notifications}
                    onCheckedChange={(checked) => updateCommunicationPref('sms_notifications', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between space-x-2 border p-2 rounded-md bg-card">
                  <div>
                    <Label htmlFor="class_reminders" className="text-sm">Class Reminders</Label>
                    <p className="text-xs text-muted-foreground">Reminders for upcoming classes</p>
                  </div>
                  <Switch
                    id="class_reminders"
                    checked={communicationPrefs.class_reminders}
                    onCheckedChange={(checked) => updateCommunicationPref('class_reminders', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between space-x-2 border p-2 rounded-md bg-card">
                  <div>
                    <Label htmlFor="billing_alerts" className="text-sm">Billing Alerts</Label>
                    <p className="text-xs text-muted-foreground">Notifications for invoices and payments</p>
                  </div>
                  <Switch
                    id="billing_alerts"
                    checked={communicationPrefs.billing_alerts}
                    onCheckedChange={(checked) => updateCommunicationPref('billing_alerts', checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between space-x-2 border p-2 rounded-md bg-card">
                  <div>
                    <Label htmlFor="event_announcements" className="text-sm">Event Announcements</Label>
                    <p className="text-xs text-muted-foreground">News about events and activities</p>
                  </div>
                  <Switch
                    id="event_announcements"
                    checked={communicationPrefs.event_announcements}
                    onCheckedChange={(checked) => updateCommunicationPref('event_announcements', checked)}
                  />
                </div>
              </div>
            </div>

            {/* Preferred Contact Method */}
            <div className="space-y-2">
              <Label htmlFor="preferred_contact_method">Preferred Contact Method</Label>
              <Select
                value={communicationPrefs.preferred_contact_method}
                onValueChange={(value: 'email' | 'sms' | 'both') => updateCommunicationPref('preferred_contact_method', value)}
              >
                <SelectTrigger id="preferred_contact_method">
                  <SelectValue placeholder="Select preferred method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email Only</SelectItem>
                  <SelectItem value="sms">SMS Only</SelectItem>
                  <SelectItem value="both">Both Email & SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Information & Danger Zone */}
      <Card className={styleHelpers.getCardClasses(false, "")}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <h3 className={visualStyles.typography.cardTitle}>Additional Information</h3>
          </div>
          
          <div className="grid gap-6">
            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                rows={3}
                placeholder="Any additional information about this parent..."
              />
            </div>

            {/* Danger Zone */}
            {onDelete && (
              <div className="border-t pt-6">
                <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
                  <h3 className="text-destructive font-medium text-sm mb-2">Danger Zone</h3>
                  <p className="text-sm text-destructive mb-4">
                    The actions below are destructive and cannot be undone. Please be careful.
                  </p>
                  
                  <div className="border border-destructive/20 rounded-md p-4 bg-white">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-sm">Delete this parent</h4>
                        <p className="text-xs text-gray-500">
                          This will remove the parent from this family and revoke their access to the parent portal.
                        </p>
                      </div>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm" className="gap-1.5">
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Parent</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete {parent.first_name} {parent.last_name}?
                              This action will remove them from the family and revoke their access to the parent portal.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleDelete}
                              className="bg-destructive hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentFormFields;